import React, { useState, useEffect } from 'react';
import { Avatar } from '@mui/material';
import { getAllCredentialsByIssuer, updateCredential } from '../../../services/apiservice';
import { useAuth } from '../../../contexts/authContext';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import CredentialEditModel from './CredentialEditModel';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

function CredentialList() {
  const { user } = useAuth();
  const CredentialIssuerID = user.role === 'Admin' ? user.UserID : user.IssuerID;
  const { data: fetchedCredentials, loading } = useApiData(() => getAllCredentialsByIssuer(CredentialIssuerID).then((res) => res.data), [CredentialIssuerID]);
  const [credentials, setCredentials] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modelOpen, setModalOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);

  useEffect(() => {
    if (fetchedCredentials) {
      setCredentials(fetchedCredentials);
    }
  }, [fetchedCredentials]);

  const handleEdit = (credential) => {
    setSelectedCredential(credential);
    setModalOpen(true);
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCredential(null);
  };

  const handleSave = async (updatedCredential) => {
    try {
      const response = await updateCredential(updatedCredential);
      if (response.success) {
        setCredentials((prevCredentials) =>
          prevCredentials.map((credential) =>
            credential.CredentialID === updatedCredential.CredentialID
              ? { ...credential, ...updatedCredential }
              : credential
          )
        );
        setSnackbarMessage('Credential updated successfully!');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'CredentialID', label: 'Credential ID', width: 115 },
    { key: 'CredentialName', label: 'Credential Name', width: 200 },
    { key: 'CredentialDescription', label: 'Credential Description', width: 250 },
    { key: 'CredentialCategory', label: 'Credential Category', width: 160 },
    {
      key: 'logo',
      label: 'Credential Logo',
      width: 150,
      render: (credential) => (
        credential.CredentialImageURL ? (
          <Avatar src={credential.CredentialImageURL} sx={{ width: 40, height: 40 }} />
        ) : (
          <Avatar sx={{ width: 40, height: 40, backgroundColor: 'grey.300' }} />
        )
      ),
    },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Credentials List"
        columns={columns}
        rows={credentials}
        loading={loading}
        searchKeys={['CredentialName', 'CredentialDescription', 'CredentialCategory']}
        rowKey="CredentialID"
        onEdit={handleEdit}
      />
      <CredentialEditModel
        open={modelOpen}
        onClose={handleModalClose}
        credential={selectedCredential}
        onSave={handleSave}
      />
    </>
  );
}

export default CredentialList;
